import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, PenLine, Search, Mail, Download } from "lucide-react";
import { PROPOSAL_LABEL, PROPOSAL_CLS, ProposalDecision, CountsEditor, countsText, hasCounts } from "@/components/ServiceReport";

export default function Protocols({ svjId, actionId, actionName }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.get(`/svjs/${svjId}/protocols`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))), [svjId]);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <p className="text-slate-500 mt-4">Načítání…</p>;
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const n = norm(q);
  const list = rows.filter((r) => {
    if (filter === "pending" && r.report?.proposal_status !== "pending") return false;
    if (filter === "proposal" && !r.report?.proposal) return false;
    if (filter === "unsigned" && r.report?.resident_signed) return false;
    return !n || norm(`${r.apartment} ${r.owner_name} ${r.name} ${r.entrance}`).includes(n);
  });
  const pdf = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/reservations/${id}/report.pdf`;
  const sendResident = async (r) => {
    try { const { data } = await api.post(`/reservations/${r.id}/report/send-resident`); toast.success(`Protokol odeslán na ${data.sent_to}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const pending = rows.filter((r) => r.report?.proposal_status === "pending").length;
  const exportAll = async () => {
    try {
      const res = await api.get(`/actions/${actionId}/protocols.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `protokoly_${(actionName || "akce").replace(/\s+/g, "_")}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.status === 404 ? "Akce zatím nemá žádné protokoly" : apiErr(e)); }
  };
  return (
    <div className="mt-4 space-y-4" data-testid="protocols-tab">
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-600 flex-1"><b>{rows.length}</b> protokolů{pending ? <> · <span className="text-amber-700 font-medium">{pending} čeká na vaše schválení</span></> : null}</div>
        <div className="flex gap-1 text-xs">
          {[["all", "Vše"], ["pending", "Ke schválení"], ["proposal", "S víceprácemi"], ["unsigned", "Bez podpisu"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full border ${filter === k ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-600"}`} data-testid={`protocols-filter-${k}`}>{l}</button>
          ))}
        </div>
        <div className="relative"><Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="byt, jméno…" className="pl-8 h-9 w-48" data-testid="protocols-search" /></div>
        {actionId && <Button size="sm" onClick={exportAll} style={{ background: "#0A2540" }} data-testid="protocols-export-all"><Download className="h-4 w-4 mr-1" /> Všechny protokoly akce (PDF)</Button>}
      </div>
      {list.length === 0 && <p className="text-slate-500 text-sm" data-testid="protocols-empty">Zatím žádné protokoly.</p>}
      <div className="space-y-2">
        {list.map((r) => {
          const rep = r.report || {};
          return (
            <div key={r.id} className="bg-white rounded-xl border p-4 space-y-2" data-testid={`protocol-${r.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0A2540]">Vchod {r.entrance} · byt <span className="text-[#C5A059]">{r.apartment}</span>{r.owner_name && <span className="text-slate-600 font-normal"> · {r.owner_name}</span>}</div>
                  <div className="text-xs text-slate-500">{r.date} {r.start}–{r.end} · {r.action_name} · technik {rep.by || "—"} · přítomen {r.name || "—"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {rep.proposal && <span className={`px-2 py-0.5 rounded border ${PROPOSAL_CLS[rep.proposal_status] || ""}`} data-testid={`protocol-status-${r.id}`}>{PROPOSAL_LABEL[rep.proposal_status]}</span>}
                  <span className={`px-2 py-0.5 rounded inline-flex items-center gap-1 ${rep.resident_signed ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`protocol-signed-${r.id}`}><PenLine className="h-3 w-3" /> {rep.resident_signed ? "Podepsáno obyvatelem" : "Bez podpisu obyvatele"}</span>
                  {r.photos_count > 0 && <span className="text-slate-500">{r.photos_count} foto</span>}
                  <a href={pdf(r.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0A2540] font-medium hover:underline" data-testid={`protocol-pdf-${r.id}`}><FileText className="h-4 w-4" /> PDF</a>
                  {r.email && <button onClick={() => sendResident(r)} className="inline-flex items-center gap-1 text-slate-600 hover:text-[#0A2540] underline" title={`Poslat protokol na ${r.email}`} data-testid={`protocol-send-resident-${r.id}`}><Mail className="h-4 w-4" /> {rep.resident_emailed_at ? "Poslat obyvateli znovu" : "Poslat obyvateli"}</button>}
                </div>
              </div>
              <div className="text-xs text-slate-700" data-testid={`protocol-counts-${r.id}`}>{hasCounts(rep) ? countsText(rep) : <span className="text-amber-700">Počty (křídla, dveře, těsnění) nejsou doplněny</span>}</div>
              <CountsEditor r={r} onSaved={load} />
              <ProposalDecision r={r} onDone={load} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
